import { IProductsHasClients } from '@llws/platform-shared-types/types/db/auth';
import { IAction, IStateStrictV2 } from '@/shared/interfaces/store-base';
import { productsGroupMap } from '@/shared/helpers/productGroupsMap';
import { StoreBaseImplementationV2 } from '@/shared/classes/store-base-implementation-v2';

interface IPermsApiProductsHasClientsState extends IStateStrictV2<Partial<IProductsHasClients>> {}

const resourcePath = '/perms-api/products__has_clients';

/**
 * Replace ProductsHasClients with their groups
 * @param phc The ProductsHasClients list
 * @returns A list with productsHasClients replaced by their groups
 */
const groupProductsHasClients = (
  phc: Partial<IProductsHasClients>[],
): Partial<IProductsHasClients>[] => phc.reduce((preV: Partial<IProductsHasClients>[], curV) => {
  const key = Object.keys(productsGroupMap).find((k) => productsGroupMap[k].list.includes(curV.productKey ?? ''));
  if (!key) {
    // not in a group
    return [ ...preV, curV];
  }
  if (preV.findIndex((item) => item.productKey === key && item.clientId === curV.clientId && item.modelId === curV.modelId) < 0) {
    // in a group but not grouped yet
    // should be replaced by its group
    const group = productsGroupMap[key];
    return [ ...preV, {
      ...curV,
      productKey: key,
    }];
  }
  // already groupd
  return preV;
}, [] as Partial<IProductsHasClients>[]);

/**
 * Extract productsHasClients from their groups
 * @param phc The ProductsHasClients list
 * @returns A list with groups extracted into actural productsHasClients
 */
const ungroupProductsHasClients = (
  phc: Partial<IProductsHasClients>[],
): Partial<IProductsHasClients>[] => phc.reduce((preV: Partial<IProductsHasClients>[], curV: Partial<IProductsHasClients>) => {
  const key = Object.keys(productsGroupMap).find((k) => k === curV.productKey);
  if (key) {
    // this is a grouped element, extract it
    const group = productsGroupMap[key];
    return [
      ...preV,
      ...group.list.map((pKey) => ({
        ...curV,
        productKey: pKey,
      } as Partial<IProductsHasClients>)),
    ];
  }
  return [ ...preV, curV ];
}, [] as Partial<IProductsHasClients>[]);

const base = new StoreBaseImplementationV2<Partial<IProductsHasClients>>({
  adminGatewayResourcePath: resourcePath,
});

export default {
  namespaced: true,

  state: {
    ...base.state,
  },

  getters: {
    ...base.getters,
    groupedProductsHasClients: (state: IPermsApiProductsHasClientsState) => groupProductsHasClients(state.list?.data ?? []),
  },

  mutations: {
    ...base.mutations,
  },

  actions: {
    ...base.actions,

    insertMany: async (ctx: IAction<IPermsApiProductsHasClientsState>, productsHasClients: Partial<IProductsHasClients>[]) => {
      await base.actions.insertMany(ctx, { data: ungroupProductsHasClients(productsHasClients) });
    },

    bulkDelete: async (ctx: IAction<IPermsApiProductsHasClientsState>, productsHasClients: Partial<IProductsHasClients>[]) => {
      const deleteParams = ungroupProductsHasClients(productsHasClients).map((phc) => {
        const { productKey, clientId, modelId } = phc;
        return `productKey:${productKey}~clientId:${clientId}~modelId:${modelId}`;
      }).join(',');

      await base.actions.bulkDelete(ctx, { ids: deleteParams });
    },
  },
};
